import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MasterModalContext from 'contexts/MasterModalContext'
import {
  get as getLocalStorage,

  set as setLocalStorage,
  remove as removeLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { matchPath } from 'react-router'
import clearLoyaltyEnrolment from 'actions/loyalty/clearLoyaltyEnrolment'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import { diffInDays } from 'lib/datetime/dateUtils'
import useLoyaltyUserEligibilityTier from './useLoyaltyUserEligibilityTier'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { DISMISS_JOIN_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyStayUpgradePromoJoinModal from 'loyalty/components/StayUpgradePromo/LoyaltyStayUpgradePromoJoinModal'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/agentHubSelectors'

const pathsBlacklist = [
  '/:region/checkout',
  '/:region/booking/confirmation',
  `/:region/${LOYALTY_LANDING_PAGE}`,
  '/:region/offer',
]

function useLoyaltyEnrolExistingUserModal() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const modalState = useContext(MasterModalContext)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account)
  const [eligibilityTier, eligibilityFetching, eligibilityError] = useLoyaltyUserEligibilityTier()
  const modalDismissalDate = getLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY)
  const loyaltyUpgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)

  const isDismissedPeriod = useMemo(() => modalDismissalDate ? diffInDays(new Date(modalDismissalDate), new Date()) < 14 : false, [modalDismissalDate])

  const path = useAppSelector(state => state.router.location.pathname)

  const isModalPreventedPage = !!matchPath(path, {
    path: pathsBlacklist,
    exact: false,
  })

  const shouldShowModal = loggedIn &&
    loyaltyEnabled &&
    !isDismissedPeriod &&
    !isLuxLoyaltyEnabledInAgentHub &&
    !modalState.open &&
    !hasLoyaltyAccount &&
    !loyaltyAccount.fetching &&
    !loyaltyAccount.error &&
    !isModalPreventedPage &&
    !eligibilityFetching &&
    !eligibilityError &&
    !!eligibilityTier

  const handleClearEnrolment = useCallback(() => {
    dispatch(clearLoyaltyEnrolment())
    setLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY, new Date().toISOString())
  }, [dispatch])

  useEffect(() => {
    if (modalDismissalDate && !isDismissedPeriod) {
      removeLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY)
    }
  }, [isDismissedPeriod, modalDismissalDate])

  const showJoinModal = useCallback(async() => {
    let joinConfirmed = false

    if (loyaltyUpgradesPromo?.enabled) {
      joinConfirmed = await showModal<boolean>(<LoyaltyStayUpgradePromoJoinModal
        onAfterClose={handleClearEnrolment}
        primaryActionCta="Activate free membership"
        secondaryActionCta="Maybe later"
      />)
    } else {
      joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
        onAfterClose={handleClearEnrolment}
        primaryActionCta="Activate free membership"
        secondaryActionCta="Maybe later"
      />)
    }

    setLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY, new Date().toISOString())
    if (joinConfirmed) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [dispatch, handleClearEnrolment, showModal, loyaltyUpgradesPromo?.enabled])

  useEffect(() => {
    if (shouldShowModal) showJoinModal()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowModal])
}

export default useLoyaltyEnrolExistingUserModal
